import React, { useEffect, useRef } from "react";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import Quill from "quill";

import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { CHAT_CRM_ACTION_CLICK } from "../quill/crmActionBlot";
import { registerChatQuillBlots } from "../quill/registerChatQuillBlots";
import type {
  CrmChatRecordContext,
  ManagingPromptAction,
  QuillDelta,
} from "../types/chat.types";
import { parseCrmActionEmbed } from "../utils/crmActionDelta";
import "./chat-quill.css";

const Block: any = Quill.import("blots/block");
Block.tagName = "div";
Quill.register(Block, true);

export interface ChatQuillReadOnlyProps {
  /** Quill Delta document — sole display format for Ask Type+ messages. */
  contentDelta?: QuillDelta | null;
  /** Debounce delta updates while SSE rebuilds `contentDelta` on the client. */
  isStreaming?: boolean;
  record?: CrmChatRecordContext | null;
  onCrmActionClick?: (action: ManagingPromptAction) => void;
  className?: string;
}

const STREAM_DEBOUNCE_MS = 120;

const ChatQuillReadOnly: React.FC<ChatQuillReadOnlyProps> = ({
  contentDelta,
  isStreaming = false,
  record,
  onCrmActionClick,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const debouncedDelta = useDebouncedValue(
    contentDelta,
    isStreaming ? STREAM_DEBOUNCE_MS : 0
  );

  useEffect(() => {
    registerChatQuillBlots();
  }, []);

  useEffect(() => {
    if (!onCrmActionClick || !record?.recordId) {
      return;
    }
    const handler = (event: Event) => {
      const custom = event as CustomEvent<unknown>;
      const embed = parseCrmActionEmbed(custom.detail);
      if (!embed) {
        return;
      }
      onCrmActionClick({
        type: embed.type,
        label: embed.label,
        prefill: embed.prefill,
      });
    };
    window.addEventListener(CHAT_CRM_ACTION_CLICK, handler);
    return () => window.removeEventListener(CHAT_CRM_ACTION_CLICK, handler);
  }, [onCrmActionClick, record?.recordId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    registerChatQuillBlots();
    container.innerHTML = "";
    const quill = new Quill(container, {
      theme: "snow",
      readOnly: true,
      modules: { toolbar: false },
    });
    quillRef.current = quill;

    return () => {
      quillRef.current = null;
      container.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    if (debouncedDelta?.ops?.length) {
      quill.setContents(debouncedDelta as { ops: unknown[] }, "silent");
      return;
    }

    quill.setText("");
  }, [debouncedDelta]);

  const hasDelta = Boolean(contentDelta?.ops?.length);

  return (
    <div
      className={`chat-quill-readonly w-full ${className}`}
      aria-hidden={!hasDelta && !isStreaming}
    >
      {isStreaming && !hasDelta ? (
        <span className="text-sm text-gray-400" aria-hidden="true">
          …
        </span>
      ) : null}
      <div ref={containerRef} className="w-full" />
    </div>
  );
};

export default ChatQuillReadOnly;
